/**
 * Calendar preferences — Vague 113.
 *
 * Strategy: localStorage cache for instant render + MongoDB sync for multi-
 * device persistence. The hook returns `settings` merged from defaults, the
 * MongoDB copy (if present) and the localStorage mirror. On save it writes
 * both places in parallel.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '../api/client';

const LS_KEY = 'ifaap_calendar_settings';

export const DEFAULT_SETTINGS = {
  // Horaires & grille
  start_hour: 7,
  end_hour: 21,
  slot_minutes: 15,
  default_duration_minutes: 30,
  first_day_of_week: 0, // 0=Monday (matches our startOfWeek helper)
  // Vue & affichage
  default_view: 'month',
  show_week_numbers: false,
  hide_done: false,
  hide_cancelled: true,
  // Rappels
  default_reminders: [15],
  email_reminder: true,
  toast_reminder: true,
  // Avancés
  timezone: 'Europe/Paris',
  show_team_members: false,
  default_color: '#8E8FA5',
  // Vague 113 L2 — shared agendas toggled visible
  visible_owner_ids: [],
};

function readLS() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}
function writeLS(obj) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(obj)); } catch { /* noop */ }
}

export function useCalendarSettings() {
  const [settings, setSettings] = useState({ ...DEFAULT_SETTINGS, ...readLS() });
  const [ready, setReady] = useState(false);

  // Hydrate from backend on mount. We deliberately run this even when LS
  // has a copy so the multi-device view stays consistent.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/calendar/settings');
        const merged = { ...DEFAULT_SETTINGS, ...(data || {}) };
        setSettings(merged);
        writeLS(merged);
      } catch { /* keep local */ }
      finally { setReady(true); }
    })();
  }, []);

  const update = useCallback(async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    writeLS(next);
    try { await api.put('/calendar/settings', patch); } catch { /* backend will resync on next mount */ }
  }, [settings]);

  const reset = useCallback(async () => {
    setSettings(DEFAULT_SETTINGS);
    writeLS(DEFAULT_SETTINGS);
    try { await api.delete('/calendar/settings'); } catch { /* noop */ }
  }, []);

  return { settings, update, reset, ready };
}
